/* =====================================================================
   Cliente da FIPE via gateway CloudFront (infra compartilhada — mesmo
   proxy usado nos embeds). A FIPE oficial fica atrás do Cloudflare e
   bloqueia IP de nuvem (403); o gateway injeta os headers e força egress
   BR. Todas as chamadas são POST `application/x-www-form-urlencoded` e o
   gateway responde com CORS liberado, então o bloco chama direto do
   client-side (builder e site publicado), sem backend próprio.
   ===================================================================== */

const FIPE_GATEWAY = 'https://d3b64s2f2lshf4.cloudfront.net/fipe';

export type FipeVehicleType = '1' | '2' | '3'; // 1 = carro · 2 = moto · 3 = caminhão

export interface FipeOption {
  label: string;
  value: string;
}

/** `codigoTipoVeiculo` → `tipoVeiculo` (string exigida por ConsultarValor…). */
const TIPO_VEICULO_LABEL: Record<FipeVehicleType, string> = {
  '1': 'carro',
  '2': 'moto',
  '3': 'caminhao',
};

export const FIPE_VEHICLE_TYPES: FipeOption[] = [
  { value: '1', label: 'Carro' },
  { value: '2', label: 'Moto' },
  { value: '3', label: 'Caminhão' },
];

const postForm = async (endpoint: string, params: Record<string, string> = {}): Promise<unknown> => {
  const body = new URLSearchParams(params).toString();
  const res = await fetch(`${FIPE_GATEWAY}/${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!res.ok) throw new Error(`FIPE ${endpoint} respondeu ${res.status}`);
  return res.json();
};

const toOption = (o: { Label?: unknown; Value?: unknown }): FipeOption => ({
  label: String(o?.Label ?? ''),
  value: String(o?.Value ?? ''),
});

/** Código da tabela de referência do mês atual (muda todo mês). */
export const fetchReferenceCode = async (): Promise<string> => {
  const data = (await postForm('ConsultarTabelaDeReferencia')) as Array<{ Codigo?: unknown }>;
  return String(data?.[0]?.Codigo ?? '');
};

export const fetchBrands = async (ref: string, tipo: FipeVehicleType): Promise<FipeOption[]> => {
  const data = (await postForm('ConsultarMarcas', {
    codigoTabelaReferencia: ref,
    codigoTipoVeiculo: tipo,
  })) as Array<{ Label?: unknown; Value?: unknown }>;
  return Array.isArray(data) ? data.map(toOption) : [];
};

export const fetchModels = async (ref: string, tipo: FipeVehicleType, brandCode: string): Promise<FipeOption[]> => {
  const data = (await postForm('ConsultarModelos', {
    codigoTabelaReferencia: ref,
    codigoTipoVeiculo: tipo,
    codigoMarca: brandCode,
  })) as { Modelos?: Array<{ Label?: unknown; Value?: unknown }> };
  return Array.isArray(data?.Modelos) ? data.Modelos.map(toOption) : [];
};

/** Anos disponíveis. `value` = "ano-combustivel" (ex.: "1987-1"). */
export const fetchYears = async (
  ref: string,
  tipo: FipeVehicleType,
  brandCode: string,
  modelCode: string,
): Promise<FipeOption[]> => {
  const data = (await postForm('ConsultarAnoModelo', {
    codigoTabelaReferencia: ref,
    codigoTipoVeiculo: tipo,
    codigoMarca: brandCode,
    codigoModelo: modelCode,
  })) as Array<{ Label?: unknown; Value?: unknown }>;
  return Array.isArray(data) ? data.map(toOption) : [];
};

/** Valor FIPE (número) para o ano selecionado. `yearValue` = "ano-combustivel". */
export const fetchValue = async (
  ref: string,
  tipo: FipeVehicleType,
  brandCode: string,
  modelCode: string,
  yearValue: string,
): Promise<number> => {
  const [anoModelo, codigoTipoCombustivel = '1'] = yearValue.split('-');
  const data = (await postForm('ConsultarValorComTodosParametros', {
    codigoTabelaReferencia: ref,
    codigoTipoVeiculo: tipo,
    codigoMarca: brandCode,
    codigoModelo: modelCode,
    anoModelo,
    codigoTipoCombustivel,
    tipoVeiculo: TIPO_VEICULO_LABEL[tipo],
    tipoConsulta: 'tradicional',
  })) as { Valor?: unknown };
  return parseBRL(String(data?.Valor ?? ''));
};

/** "R$ 6.147,00" → 6147. */
export const parseBRL = (v: string): number => {
  const digits = v.replace(/[^\d,]/g, '').replace(/\./g, '').replace(',', '.');
  const n = Number(digits);
  return Number.isFinite(n) ? Math.round(n) : 0;
};
